"use client";

import type { Dispatch, SetStateAction } from "react";
import dynamic from "next/dynamic";
import { buildEventPath } from "@/lib/routes";
import { BuyerFormModal } from "./components/BuyerFormModal";

import type { CoverChargeCategory, PendingOrder, PromoValidation } from "./components/TicketPickerModal";

type FormFieldDef = {
  id: string;
  type: string;
  label: string;
  required: boolean;
  placeholder?: string;
  helpText?: string;
  options?: string[];
  validation?: Record<string, unknown>;
  sortOrder: number;
};

/* Dynamic modal imports — code-split, no SSR */
const GalleryModal = dynamic(() => import("./components/GalleryModal").then((m) => ({ default: m.GalleryModal })), { ssr: false });
const TicketPickerModal = dynamic(() => import("./components/TicketPickerModal").then((m) => ({ default: m.TicketPickerModal })), { ssr: false });
const DummyPaymentModal = dynamic(() => import("./components/DummyPaymentModal").then((m) => ({ default: m.DummyPaymentModal })), { ssr: false });
const TicketQrModal = dynamic(() => import("./components/TicketQrModal").then((m) => ({ default: m.TicketQrModal })), { ssr: false });
const CancelRsvpModal = dynamic(() => import("./components/CancelRsvpModal").then((m) => ({ default: m.CancelRsvpModal })), { ssr: false });
const CashOnlyModal = dynamic(() => import("./components/CashOnlyModal").then((m) => ({ default: m.CashOnlyModal })), { ssr: false });
const ReportModal = dynamic(() => import("./components/ReportModal").then((m) => ({ default: m.ReportModal })), { ssr: false });
const AttendeesModal = dynamic(() => import("./components/AttendeesModal").then((m) => ({ default: m.AttendeesModal })), { ssr: false });
const AuthModal = dynamic(() => import("./components/AuthModal").then((m) => ({ default: m.AuthModal })), { ssr: false });
const ProfileGateModal = dynamic(() => import("./components/ProfileGateModal").then((m) => ({ default: m.ProfileGateModal })), { ssr: false });

interface EventModalsProps {
  // Gallery
  galleryOpen: boolean;
  setGalleryOpen: (v: boolean) => void;
  mediaItems: { id: string; kind: string; url: string; label: string | null }[];
  galleryIndex: number;
  setGalleryIndex: (updater: (i: number) => number) => void;

  // Ticket Picker
  ticketPickerOpen: boolean;
  setTicketPickerOpen: (v: boolean) => void;
  setCheckoutStep: (v: "select" | "form" | "review" | "guest-info") => void;
  setPendingOrder: (v: PendingOrder | null) => void;
  setPromoValidation: (v: PromoValidation | null) => void;
  formResponses: Record<string, unknown>;
  setFormResponses: (v: Record<string, unknown>) => void;
  ticketOptions: { id: string; label: string; price: string | null; originalPrice: string | null; description: string | null; quantityTotal: number | null; quantitySold: number | null; startsAt: string | null; expiresAt: string | null; tierStatus: "active" | "upcoming" | "expired" | "sold_out" }[];
  coverChargeCategory: CoverChargeCategory | null;
  selectedTickets: { id: string; quantity: number }[];
  setSelectedTickets: Dispatch<SetStateAction<{ id: string; quantity: number }[]>>;
  promoCode: string;
  setPromoCode: (v: string) => void;
  promoValidation: PromoValidation | null;
  promoValidating: boolean;
  checkoutStep: "select" | "form" | "review" | "guest-info";
  pendingOrder: PendingOrder | null;
  maxTicketsPerOrder: number;
  ticketCategories?: {
    id: string;
    priceCents: number | null;
    inclusivePriceCents?: number | null;
    currency: string | null;
    gstRate?: number | null;
    coverChargeType?: string | null;
    coverChargeValue?: number | null;
  }[];
  rsvpState: string;
  rsvpError: string | null;
  canContinueCheckout: boolean;
  onApplyPromo: () => void;
  onTicketCheckout: () => void;
  onConfirmPayment: () => void;

  // Dummy Payment
  dummyPaymentOpen: boolean;
  setDummyPaymentOpen: (v: boolean) => void;
  dummyPaymentOrder: { orderId: string; currency: string } | null;
  setDummyPaymentOrder: (v: { orderId: string; currency: string } | null) => void;
  dummyPaymentError: string | null;
  setDummyPaymentError: (v: string | null) => void;
  dummyPaymentLoading: boolean;
  setRsvpState: (v: "idle" | "loading" | "confirmed" | "approved" | "pending" | "waitlist" | "rejected" | "cancelled" | "error") => void;
  onDummyPaymentSubmit: (cardName: string, cardNumber: string, cardExpiry: string, cardCvc: string) => void;

  // Ticket QR
  ticketModalOpen: boolean;
  setTicketModalOpen: (v: boolean) => void;
  ticketQrUrl: string | null;
  ticketCode: string | null;
  ticketRsvpId: string | null;
  purchasedTickets: { rsvpId: string; ticketCode: string | null; tierLabel: string | null }[];
  setPurchasedTickets: (v: { rsvpId: string; ticketCode: string | null; tierLabel: string | null }[]) => void;

  // Cancel RSVP
  cancelConfirmOpen: boolean;
  setCancelConfirmOpen: (v: boolean) => void;
  isPaidEvent: boolean;
  onCancelRsvp: () => void;

  // Cash Only
  cashOnlyOpen: boolean;
  setCashOnlyOpen: (v: boolean) => void;
  onCashOnlyConfirm: () => void;

  // Report
  reportOpen: boolean;
  setReportOpen: (v: boolean) => void;
  reportReason: string;
  setReportReason: (v: string) => void;
  reportNotes: string;
  setReportNotes: (v: string) => void;
  reportEmail: string;
  setReportEmail: (v: string) => void;
  reportError: string | null;
  reportStatus: "idle" | "submitting" | "sent" | "error";
  isSignedIn: boolean | undefined;
  onSubmitReport: () => void;

  // Attendees
  attendeesOpen: boolean;
  setAttendeesOpen: (v: boolean) => void;
  attendees: { id: string; name: string; imageUrl: string | null }[];
  attendeesLoading: boolean;

  // Auth
  showAuthModal: boolean;
  setShowAuthModal: (v: boolean) => void;
  authModalMessage: string | null;
  setAuthModalMessage: (v: string | null) => void;
  eventId: string;
  inviteId: string;
  router: { push: (url: string) => void };

  // Profile Gate
  showProfileGate: boolean;
  setShowProfileGate: (v: boolean) => void;
  onProfileGateComplete: (socials: { instagramHandle?: string; xHandle?: string; snapchatHandle?: string; emailUpdated?: string }) => void;
  onProfileGateClose: () => void;
  onContinueAsGuest: () => void;
  dbEmail: string | null;
  savedSocialLinks: Record<string, string>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  event: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  user: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getToken: () => Promise<string | null>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  signIn: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setActive: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  signOut: any;

  // In-App Browser Warning
  showInAppBrowserWarning: boolean;
  setShowInAppBrowserWarning: (v: boolean) => void;
  copiedLink: boolean;
  setCopiedLink: (v: boolean) => void;

  // Buyer Form
  showBuyerForm: boolean;
  setShowBuyerForm: (v: boolean) => void;
  formFields: FormFieldDef[];
  setFormFields: (v: FormFieldDef[]) => void;
  eventKey: string;
  pendingTicketForForm: { code: string | null; rsvpId: string | null; tickets?: { rsvpId: string; ticketCode: string | null; tierLabel: string | null }[] } | null;
  setPendingTicketForForm: (v: { code: string | null; rsvpId: string | null; tickets?: { rsvpId: string; ticketCode: string | null; tierLabel: string | null }[] } | null) => void;
  onShowTicketQr: (code: string | null, rsvpId: string | null) => void;
}

export function EventModals(props: EventModalsProps) {
  return (
    <>
      <GalleryModal
        open={props.galleryOpen}
        onClose={() => props.setGalleryOpen(false)}
        mediaItems={props.mediaItems}
        galleryIndex={props.galleryIndex}
        setGalleryIndex={props.setGalleryIndex as (updater: (i: number) => number) => void}
      />

      <TicketPickerModal
        open={props.ticketPickerOpen}
        onClose={() => {
          props.setTicketPickerOpen(false);
          props.setCheckoutStep("select");
          props.setPendingOrder(null);
          props.setPromoValidation(null);
          props.setFormResponses({});
        }}
        eventId={props.eventId}
        ticketOptions={props.ticketOptions}
        coverChargeCategory={props.coverChargeCategory}
        selectedTickets={props.selectedTickets}
        setSelectedTickets={props.setSelectedTickets}
        promoCode={props.promoCode}
        setPromoCode={props.setPromoCode}
        promoValidation={props.promoValidation}
        setPromoValidation={props.setPromoValidation}
        promoValidating={props.promoValidating}
        checkoutStep={props.checkoutStep}
        setCheckoutStep={props.setCheckoutStep}
        formFields={props.formFields}
        formResponses={props.formResponses}
        setFormResponses={props.setFormResponses}
        pendingOrder={props.pendingOrder}
        setPendingOrder={props.setPendingOrder}
        maxTicketsPerOrder={props.maxTicketsPerOrder}
        ticketCategories={props.ticketCategories}
        rsvpState={props.rsvpState}
        rsvpError={props.rsvpError}
        canContinueCheckout={props.canContinueCheckout}
        onApplyPromo={props.onApplyPromo}
        onTicketCheckout={props.onTicketCheckout}
        onConfirmPayment={props.onConfirmPayment}
        onGuestCheckoutComplete={() => {
          // After the guest-info form completes, re-enter handleTicketCheckout
          // which will detect the sessionToken in sessionStorage and take the
          // guest payment path (checkout-session/create-order → Razorpay →
          // checkout-session/verify-payment). US-4.1: no Clerk account required.
          props.onTicketCheckout();
        }}
      />

      <DummyPaymentModal
        open={props.dummyPaymentOpen}
        onClose={() => { props.setDummyPaymentOpen(false); props.setDummyPaymentOrder(null); props.setDummyPaymentError(null); props.setRsvpState("idle"); }}
        order={props.dummyPaymentOrder}
        loading={props.dummyPaymentLoading}
        error={props.dummyPaymentError}
        onSubmit={props.onDummyPaymentSubmit}
      />

      <TicketQrModal
        open={props.ticketModalOpen}
        onClose={() => { props.setTicketModalOpen(false); props.setPurchasedTickets([]); }}
        ticketQrUrl={props.ticketQrUrl}
        ticketCode={props.ticketCode}
        ticketRsvpId={props.ticketRsvpId}
        tickets={props.purchasedTickets.length > 0 ? props.purchasedTickets : undefined}
      />

      <CancelRsvpModal
        open={props.cancelConfirmOpen}
        onClose={() => props.setCancelConfirmOpen(false)}
        isPaidEvent={props.isPaidEvent}
        loading={props.rsvpState === "loading"}
        onConfirm={props.onCancelRsvp}
      />

      <CashOnlyModal
        open={props.cashOnlyOpen}
        onClose={() => props.setCashOnlyOpen(false)}
        loading={props.rsvpState === "loading"}
        onConfirm={props.onCashOnlyConfirm}
      />

      <ReportModal
        open={props.reportOpen}
        onClose={() => props.setReportOpen(false)}
        reportReason={props.reportReason}
        setReportReason={props.setReportReason}
        reportNotes={props.reportNotes}
        setReportNotes={props.setReportNotes}
        reportEmail={props.reportEmail}
        setReportEmail={props.setReportEmail}
        reportError={props.reportError}
        reportStatus={props.reportStatus}
        isSignedIn={props.isSignedIn}
        onSubmit={props.onSubmitReport}
      />

      <AttendeesModal
        open={props.attendeesOpen}
        onClose={() => props.setAttendeesOpen(false)}
        attendees={props.attendees}
        loading={props.attendeesLoading}
      />

      <AuthModal
        open={props.showAuthModal}
        onClose={() => { props.setShowAuthModal(false); props.setAuthModalMessage(null); }}
        message={props.authModalMessage}
        isPaidEvent={props.isPaidEvent}
        onSignIn={() => {
          const target = props.eventId ? `${buildEventPath(props.eventId, "event")}${props.inviteId ? `?invite=${props.inviteId}` : ""}` : "/";
          props.router.push(`/auth?redirect=${encodeURIComponent(target)}`);
        }}
      />

      <ProfileGateModal
        open={props.showProfileGate}
        onClose={props.onProfileGateClose}
        onComplete={props.onProfileGateComplete}
        needsEmail={(() => {
          const email = props.dbEmail || props.user?.primaryEmailAddress?.emailAddress;
          return !email || email.endsWith("@phone.hopp.co.in");
        })()}
        dbEmail={props.dbEmail || props.user?.primaryEmailAddress?.emailAddress || null}
        userName={props.user?.fullName ?? props.user?.firstName ?? null}
        requiredSocialPlatforms={props.event?.requiredSocialPlatforms ?? []}
        mandatorySocialPlatforms={props.event?.mandatorySocialPlatforms ?? []}
        minRequiredSocials={props.event?.minRequiredSocials ?? 1}
        initialSocials={props.savedSocialLinks}
        getToken={props.getToken}
        signIn={props.signIn}
        setActive={props.setActive}
        signOut={props.signOut}
        allowGuest={
          props.isPaidEvent &&
          (props.event?.requiredSocialPlatforms?.length ?? 0) === 0 &&
          !props.event?.restricted
        }
        onContinueAsGuest={props.onContinueAsGuest}
      />

      {/* In-App Browser Warning */}
      {props.showInAppBrowserWarning ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/45 backdrop-blur-md" onClick={() => props.setShowInAppBrowserWarning(false)} />
          <div className="relative w-full max-w-md rounded-2xl p-6 text-white shadow-2xl bg-[var(--hopp-panel)]">
            <p className="text-[10px] uppercase tracking-[0.35em] text-[var(--hopp-muted)]">Browser required</p>
            <h2 className="mt-2 text-xl font-semibold text-white">Open in your browser</h2>
            <p className="mt-2 text-sm text-white/70">Payments don&apos;t work inside in-app browsers. Copy the link below and paste it in Safari or Chrome.</p>
            <div className="mt-6 flex flex-col gap-3">
              <button onClick={() => { void navigator.clipboard.writeText(window.location.href); props.setCopiedLink(true); setTimeout(() => props.setCopiedLink(false), 2000); }} className="w-full rounded-full px-4 py-3 min-h-[44px] text-[10px] uppercase tracking-[0.3em] font-semibold text-[#f5f2ea] bg-[var(--hopp-sapphire)] shadow-[0_10px_26px_rgba(58,92,255,0.3)]">
                {props.copiedLink ? "Copied! Now paste in your browser" : "Copy link"}
              </button>
              <button onClick={() => props.setShowInAppBrowserWarning(false)} className="w-full rounded-full px-4 py-3 min-h-[44px] text-[10px] uppercase tracking-[0.3em] text-[var(--hopp-muted)] hover:text-white bg-[rgba(28,28,32,0.6)]">Dismiss</button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Buyer Form Modal */}
      {props.showBuyerForm && props.formFields.length > 0 && (
        <BuyerFormModal
          eventId={props.eventKey}
          fields={props.formFields}
          onComplete={() => {
            props.setShowBuyerForm(false); props.setFormFields([]);
            if (props.pendingTicketForForm?.tickets && props.pendingTicketForForm.tickets.length > 0) {
              props.setPurchasedTickets(props.pendingTicketForForm.tickets);
              props.setTicketModalOpen(true);
            } else if (props.pendingTicketForForm?.code) {
              props.onShowTicketQr(props.pendingTicketForForm.code, props.pendingTicketForForm.rsvpId);
            }
            props.setPendingTicketForForm(null);
          }}
          onSkip={() => { props.setShowBuyerForm(false); props.setFormFields([]); props.setPendingTicketForForm(null); }}
        />
      )}
    </>
  );
}
